/**
 * Authentication guard component for the admin (Google OAuth) strategy.
 *
 * Portal authentication is enforced inside the portal route components
 * (ApprovalView, EditorView, CreatorApp) via the magic-link exchange
 * flow - this guard only covers admin routes. A logged-out visitor goes to
 * the one login page, or into an existing portal session.
 */

import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppStore } from '@/core/store/useAppStore';
import { useTokenValidation } from '@/hooks/useTokenValidation';
import {
  guardState,
  loggedOutTarget,
  refreshablePortalSessionsWithCleanup,
} from './loggedOutTarget';

interface AuthGuardProps {
  /**
   * Custom fallback component while loading.
   */
  fallback?: React.ReactNode;

  /**
   * Content to protect.
   */
  children: React.ReactNode;
}

const LoadingSpinner: React.FC = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
  </div>
);

export const AuthGuard: React.FC<AuthGuardProps> = ({
  fallback,
  children,
}) => {
  const location = useLocation();
  const user = useAppStore((state) => state.user);
  const hasHydrated = useAppStore((state) => state._hasHydrated);
  const signedOutByUser = useAppStore((state) => state.signedOutByUser);
  const { isAuthenticated } = useTokenValidation();
  const state = guardState(user, isAuthenticated);
  const loggedOut = state === "out";
  // Cleans stale portal tokens (writes localStorage), so run it once per
  // signed-in/out change, not on every render.
  const portalSessions = useMemo(
    () => (loggedOut ? refreshablePortalSessionsWithCleanup() : []),
    [loggedOut]
  );

  // Wait for Zustand persist middleware to hydrate from localStorage, and
  // for the background refresh of an expired access token to settle.
  if (!hasHydrated || state === "refreshing") {
    return <>{fallback || <LoadingSpinner />}</>;
  }

  if (loggedOut) {
    return (
      <Navigate
        to={loggedOutTarget(
          location.pathname,
          location.search,
          portalSessions,
          signedOutByUser
        )}
        replace
      />
    );
  }

  return <>{children}</>;
};

export default AuthGuard;
